import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useConfirmation } from '@/components/shared/ZCard';
import {
  Plus, X, BookOpen, Search, ChevronRight, Star, Check,
  Loader2, Users, List, Tag, Sparkles, RefreshCw, Archive
} from 'lucide-react';
import ZEmptyState from '@/components/shared/ZEmptyState';
import OverflowMenu from '@/components/shared/OverflowMenu';

const ACCENT = '#C0A96A';
const BG = '#0a0a0f';
const CARD = 'rgba(255,255,255,0.03)';
const BORDER = 'rgba(255,255,255,0.06)';
const TEXT = '#f0f0f5';
const TEXT2 = '#b0b4c0';
const TEXT3 = '#555555';

const PROPERTY_COLORS = {
  core:      ACCENT,
  secondary: '#7B9EA8',
  absence:   '#107270',
};

function PropertyPill({ id, category, label, small }) {
  const color = PROPERTY_COLORS[category] || ACCENT;
  return (
    <span style={{
      display: 'inline-block',
      padding: small ? '1px 6px' : '2px 8px',
      borderRadius: 4,
      fontSize: small ? 9 : 10,
      letterSpacing: '0.08em',
      fontFamily: "'JetBrains Mono', monospace",
      background: `${color}18`,
      color,
      border: `1px solid ${color}33`,
    }}>
      {label || id}
    </span>
  );
}

function StatBadge({ value, label }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, color: ACCENT, fontWeight: 600 }}>{value}</div>
      <div style={{ fontSize: 10, color: TEXT3, letterSpacing: '0.10em', textTransform: 'uppercase', marginTop: 2 }}>{label}</div>
    </div>
  );
}

// ── Author Card ────────────────────────────────────────────────────────────────
function AuthorCard({ author, bookCount, onClick, properties }) {
  const authorProps = (author.properties || []).slice(0, 3).map(pid => {
    const p = properties.find(x => x.id === pid);
    return p || { id: pid, label: pid, category: 'core' };
  });

  return (
    <button
      onClick={onClick}
      style={{
        background: CARD,
        border: `1px solid ${BORDER}`,
        borderRadius: 10,
        padding: '16px',
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'border-color 0.2s, background 0.2s',
        width: '100%',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = `${ACCENT}55`; e.currentTarget.style.background = '#181818'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.background = CARD; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 14, color: TEXT, fontWeight: 600, lineHeight: 1.3 }}>
          {author.name}
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {author.full_works && (
            <span title="Full Works" style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 18, height: 18, borderRadius: '50%',
              background: `${ACCENT}22`, color: ACCENT, fontSize: 9,
              border: `1px solid ${ACCENT}44`, fontFamily: "'JetBrains Mono', monospace",
            }}>
              ✦
            </span>
          )}
        </div>
      </div>

      {author.notes && (
        <div style={{ fontSize: 10, color: TEXT3, marginBottom: 8, fontStyle: 'italic' }}>{author.notes}</div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
        {authorProps.map(p => (
          <PropertyPill key={p.id} id={p.id} category={p.category} label={p.label} small />
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TEXT3 }}>
          {bookCount} {bookCount === 1 ? 'book' : 'books'}
        </span>
        <ChevronRight size={12} color={TEXT3} />
      </div>
    </button>
  );
}

// ── Book Row ───────────────────────────────────────────────────────────────────
function BookRow({ book, authorName, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 14px', borderRadius: 8,
        background: 'transparent', border: 'none',
        cursor: 'pointer', width: '100%', textAlign: 'left',
        transition: 'background 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = '#181818'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      {/* Cover placeholder */}
      <div style={{
        width: 36, height: 52, borderRadius: 4, flexShrink: 0,
        background: `${ACCENT}22`, border: `1px solid ${ACCENT}33`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
      }}>
        {book.cover_url ? (
          <img src={book.cover_url} alt={book.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <BookOpen size={14} color={ACCENT} />
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 13, color: TEXT, fontWeight: 600 }}>
            {book.title}
          </span>
          {book.foundational && (
            <Star size={10} color={ACCENT} fill={ACCENT} />
          )}
        </div>
        {authorName && (
          <div style={{ fontSize: 11, color: TEXT2, marginTop: 1 }}>{authorName}</div>
        )}
        {book.series && (
          <div style={{ fontSize: 10, color: TEXT3, fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>
            {book.series}{book.series_order ? ` #${book.series_order}` : ''}
          </div>
        )}
      </div>
      <ChevronRight size={12} color={TEXT3} />
    </button>
  );
}

// ── Book Detail Panel ──────────────────────────────────────────────────────────
function BookPanel({ book, authorName, properties, onClose, onUpdate, onDelete }) {
  const feedback = useConfirmation();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...book });
  const [saving, setSaving] = useState(false);

  const bookProps = (book.properties || []).map(pid => {
    const p = properties.find(x => x.id === pid);
    return p || { id: pid, label: pid, category: 'core' };
  });

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put(`/library/books/${book.id}`, form);
      onUpdate(data);
      setEditing(false);
      feedback.confirm();
    } catch {
      feedback.fail();
    } finally {
      setSaving(false);
    }
  }

  async function archiveBook() {
    onDelete(book.id);
    onClose();
    try {
      await api.delete(`/library/books/${book.id}`);
      feedback.confirm();
    } catch {
      feedback.fail();
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
    }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div style={{
        position: 'relative', zIndex: 1,
        width: '100%', maxWidth: 480,
        height: '90vh',
        background: '#111', borderLeft: `1px solid ${BORDER}`,
        borderTop: `1px solid ${BORDER}`,
        borderTopLeftRadius: 16,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: TEXT, fontWeight: 700 }}>
                  {book.title}
                </span>
                {book.foundational && <Star size={14} color={ACCENT} fill={ACCENT} />}
              </div>
              {authorName && <div style={{ fontSize: 12, color: TEXT2, marginTop: 2 }}>{authorName}</div>}
              {book.series && (
                <div style={{ fontSize: 10, color: TEXT3, fontFamily: "'JetBrains Mono', monospace", marginTop: 4 }}>
                  {book.series}{book.series_order ? ` #${book.series_order}` : ''}
                  {book.year ? ` · ${book.year}` : ''}
                </div>
              )}
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEXT2, padding: 4 }}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {book.cover_url && (
            <div style={{ marginBottom: 20 }}>
              <img src={book.cover_url} alt={book.title} style={{
                width: 80, height: 120, objectFit: 'cover', borderRadius: 6,
                border: `1px solid ${BORDER}`,
              }} />
            </div>
          )}

          {/* Properties */}
          {bookProps.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 10, color: TEXT3, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8, fontFamily: "'JetBrains Mono', monospace" }}>
                Reading Properties
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {bookProps.map(p => (
                  <PropertyPill key={p.id} id={p.id} category={p.category} label={p.label} />
                ))}
              </div>
            </div>
          )}

          {/* Threads */}
          {(book.threads || []).length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 10, color: TEXT3, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8, fontFamily: "'JetBrains Mono', monospace" }}>
                Threads
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {book.threads.map(t => (
                  <span key={t} style={{
                    padding: '2px 8px', borderRadius: 4, fontSize: 10,
                    fontFamily: "'JetBrains Mono', monospace",
                    background: '#ffffff08', color: TEXT2, border: `1px solid ${BORDER}`,
                  }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {!editing && book.notes && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 10, color: TEXT3, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8, fontFamily: "'JetBrains Mono', monospace" }}>
                Notes
              </div>
              <div style={{ fontSize: 13, color: TEXT2, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{book.notes}</div>
            </div>
          )}

          {/* Codex Links */}
          {(book.codex_links || []).length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 10, color: TEXT3, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 8, fontFamily: "'JetBrains Mono', monospace" }}>
                Codex Links
              </div>
              {book.codex_links.map(link => (
                <div key={link} style={{ fontSize: 11, color: '#4B5FD4', fontFamily: "'JetBrains Mono', monospace", padding: '2px 0' }}>
                  → {link}
                </div>
              ))}
            </div>
          )}

          {/* Foundational note */}
          {book.foundational && (
            <div style={{
              padding: '12px 14px', borderRadius: 8,
              background: `${ACCENT}12`, border: `1px solid ${ACCENT}33`,
              marginBottom: 20,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <Star size={12} color={ACCENT} fill={ACCENT} />
                <span style={{ fontSize: 10, color: ACCENT, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                  Foundational
                </span>
              </div>
              <div style={{ fontSize: 11, color: TEXT2 }}>
                This book left permanent architecture in Werner's thinking.
              </div>
            </div>
          )}

          {/* Edit form */}
          {editing && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 10, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 4 }}>Notes</label>
                <textarea
                  value={form.notes || ''}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={4}
                  style={{
                    width: '100%', background: '#0a0a0f', border: `1px solid ${BORDER}`,
                    borderRadius: 6, padding: '8px 10px', color: TEXT, fontSize: 13,
                    resize: 'vertical', fontFamily: 'inherit', outline: 'none',
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: 10, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 4 }}>Year Read</label>
                <input
                  type="number"
                  value={form.year || ''}
                  onChange={e => setForm(f => ({ ...f, year: e.target.value ? parseInt(e.target.value) : null }))}
                  style={{
                    width: '100%', background: '#0a0a0f', border: `1px solid ${BORDER}`,
                    borderRadius: 6, padding: '8px 10px', color: TEXT, fontSize: 13, outline: 'none',
                  }}
                />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.foundational || false}
                  onChange={e => setForm(f => ({ ...f, foundational: e.target.checked }))}
                  style={{ accentColor: ACCENT }}
                />
                <span style={{ fontSize: 12, color: TEXT2 }}>Foundational</span>
              </label>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 24px', borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 8, flexShrink: 0 }}>
          {editing ? (
            <>
              <button onClick={save} disabled={saving} style={{
                flex: 1, padding: '10px', borderRadius: 8,
                background: ACCENT, color: '#0a0a0f', border: 'none',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Save
              </button>
              <button onClick={() => setEditing(false)} style={{
                padding: '10px 16px', borderRadius: 8,
                background: 'transparent', color: TEXT2,
                border: `1px solid ${BORDER}`, fontSize: 13, cursor: 'pointer',
              }}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setEditing(true)} style={{
                flex: 1, padding: '10px', borderRadius: 8,
                background: `${ACCENT}22`, color: ACCENT,
                border: `1px solid ${ACCENT}44`, fontSize: 13, cursor: 'pointer',
              }}>
                Edit
              </button>
              <button onClick={archiveBook} style={{
                padding: '10px 16px', borderRadius: 8,
                background: 'transparent', color: '#E24B4A',
                border: `1px solid #E24B4A44`, fontSize: 13, cursor: 'pointer',
              }}>
                Archive
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Author Panel (author detail with their books) ─────────────────────────────
function AuthorPanel({ author, books, properties, allProperties, onClose, onAuthorUpdate, onBookSelect }) {
  const feedback = useConfirmation();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ notes: author.notes || '', full_works: author.full_works, pantheon: author.pantheon });
  const [saving, setSaving] = useState(false);
  const [showAddBook, setShowAddBook] = useState(false);

  const authorProps = (author.properties || []).map(pid => {
    const p = allProperties.find(x => x.id === pid);
    return p || { id: pid, label: pid, category: 'core' };
  });

  const seriesBooks = {};
  const standaloneBooks = [];
  books.forEach(b => {
    if (b.series) {
      if (!seriesBooks[b.series]) seriesBooks[b.series] = [];
      seriesBooks[b.series].push(b);
    } else {
      standaloneBooks.push(b);
    }
  });

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put(`/library/authors/${author.id}`, form);
      onAuthorUpdate(data);
      setEditing(false);
      feedback.confirm();
    } catch {
      feedback.fail();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div style={{
        position: 'relative', zIndex: 1, width: '100%', maxWidth: 520,
        height: '92vh', background: '#111',
        borderLeft: `1px solid ${BORDER}`, borderTop: `1px solid ${BORDER}`,
        borderTopLeftRadius: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: TEXT, fontWeight: 700 }}>{author.name}</span>
                {author.full_works && (
                  <span style={{
                    padding: '2px 8px', borderRadius: 4, fontSize: 9,
                    fontFamily: "'JetBrains Mono', monospace",
                    background: `${ACCENT}22`, color: ACCENT, border: `1px solid ${ACCENT}44`,
                    letterSpacing: '0.10em', textTransform: 'uppercase',
                  }}>
                    Full Works
                  </span>
                )}
              </div>
              {author.notes && <div style={{ fontSize: 11, color: TEXT2, fontStyle: 'italic' }}>{author.notes}</div>}
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEXT2, padding: 4 }}>
              <X size={18} />
            </button>
          </div>

          {authorProps.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 10 }}>
              {authorProps.map(p => (
                <PropertyPill key={p.id} id={p.id} category={p.category} label={p.label} small />
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Edit form */}
          {editing && (
            <div style={{ padding: '16px 24px', borderBottom: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <label style={{ fontSize: 10, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 4 }}>Notes</label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  style={{
                    width: '100%', background: '#0a0a0f', border: `1px solid ${BORDER}`,
                    borderRadius: 6, padding: '8px 10px', color: TEXT, fontSize: 13,
                    resize: 'vertical', fontFamily: 'inherit', outline: 'none',
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.full_works} onChange={e => setForm(f => ({ ...f, full_works: e.target.checked }))} style={{ accentColor: ACCENT }} />
                  <span style={{ fontSize: 12, color: TEXT2 }}>Full Works</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.pantheon} onChange={e => setForm(f => ({ ...f, pantheon: e.target.checked }))} style={{ accentColor: ACCENT }} />
                  <span style={{ fontSize: 12, color: TEXT2 }}>Pantheon</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={save} disabled={saving} style={{
                  padding: '8px 16px', borderRadius: 8, background: ACCENT, color: '#0a0a0f',
                  border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                  Save
                </button>
                <button onClick={() => setEditing(false)} style={{
                  padding: '8px 16px', borderRadius: 8, background: 'transparent', color: TEXT2,
                  border: `1px solid ${BORDER}`, fontSize: 12, cursor: 'pointer',
                }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Books */}
          <div style={{ padding: '12px 0' }}>
            {/* Series */}
            {Object.entries(seriesBooks).sort().map(([series, sbooks]) => (
              <div key={series}>
                <div style={{ padding: '6px 24px', fontSize: 10, color: TEXT3, letterSpacing: '0.10em', textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace" }}>
                  {series}
                </div>
                {sbooks.sort((a, b) => (a.series_order || 99) - (b.series_order || 99)).map(b => (
                  <BookRow key={b.id} book={b} onClick={() => onBookSelect(b)} />
                ))}
              </div>
            ))}

            {/* Standalone */}
            {standaloneBooks.length > 0 && (
              <div>
                {Object.keys(seriesBooks).length > 0 && (
                  <div style={{ padding: '6px 24px', fontSize: 10, color: TEXT3, letterSpacing: '0.10em', textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace" }}>
                    Standalone
                  </div>
                )}
                {standaloneBooks.map(b => (
                  <BookRow key={b.id} book={b} onClick={() => onBookSelect(b)} />
                ))}
              </div>
            )}

            {books.length === 0 && <ZEmptyState mark="L" accent={ACCENT} message="No books added yet" />}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 24px', borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={() => setShowAddBook(true)} style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background: `${ACCENT}22`, color: ACCENT,
            border: `1px solid ${ACCENT}44`, fontSize: 13, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            <Plus size={14} /> Add Book
          </button>
          {!editing && (
            <button onClick={() => setEditing(true)} style={{
              padding: '10px 16px', borderRadius: 8,
              background: 'transparent', color: TEXT2,
              border: `1px solid ${BORDER}`, fontSize: 13, cursor: 'pointer',
            }}>
              Edit
            </button>
          )}
        </div>
      </div>

      {showAddBook && (
        <AddBookModal
          authorId={author.id}
          authorName={author.name}
          properties={allProperties}
          onClose={() => setShowAddBook(false)}
          onAdded={(book) => {
            setShowAddBook(false);
            feedback.confirm();
            onBookSelect(book);
          }}
        />
      )}
    </div>
  );
}

// ── Add Book Modal ─────────────────────────────────────────────────────────────
function AddBookModal({ authorId, authorName, properties, onClose, onAdded }) {
  const [title, setTitle] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', series: '', series_order: '', notes: '',
    foundational: false, properties: [], year: '',
  });

  async function lookup() {
    if (!title.trim()) return;
    setLooking(true);
    try {
      const { data } = await api.post('/library/books/lookup', { title, author: authorName });
      setLookupResult(data);
      setForm(f => ({
        ...f,
        title: data.title || title,
        year: data.year || '',
      }));
    } catch {
      setForm(f => ({ ...f, title }));
    } finally {
      setLooking(false);
    }
  }

  async function save() {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        author_id: authorId,
        cover_url: lookupResult?.cover_url || '',
        year: form.year ? parseInt(form.year) : null,
        series: form.series || null,
        series_order: form.series_order ? parseInt(form.series_order) : null,
        notes: form.notes,
        foundational: form.foundational,
        properties: form.properties,
        threads: [],
        codex_links: [],
      };
      const { data } = await api.post('/library/books', payload);
      onAdded(data);
    } catch {
      // silently fail
    } finally {
      setSaving(false);
    }
  }

  const toggleProperty = (pid) => {
    setForm(f => ({
      ...f,
      properties: f.properties.includes(pid)
        ? f.properties.filter(x => x !== pid)
        : [...f.properties, pid],
    }));
  };

  const coreProps = properties.filter(p => p.category === 'core');
  const secProps = properties.filter(p => p.category === 'secondary');

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)' }} onClick={onClose} />
      <div style={{
        position: 'relative', zIndex: 1, background: '#111',
        border: `1px solid ${BORDER}`, borderRadius: 16,
        width: '100%', maxWidth: 480, maxHeight: '85vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, color: TEXT, fontWeight: 700 }}>
              Add Book — {authorName}
            </span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEXT2 }}><X size={16} /></button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Title lookup */}
          <div>
            <label style={{ fontSize: 10, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Title</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && lookup()}
                placeholder="Type title and press Enter or Lookup"
                style={{
                  flex: 1, background: '#0a0a0f', border: `1px solid ${BORDER}`,
                  borderRadius: 6, padding: '8px 10px', color: TEXT, fontSize: 13, outline: 'none',
                }}
              />
              <button onClick={lookup} disabled={looking} style={{
                padding: '8px 14px', borderRadius: 6, background: `${ACCENT}22`,
                color: ACCENT, border: `1px solid ${ACCENT}44`, fontSize: 12, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                {looking ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
                Lookup
              </button>
            </div>
          </div>

          {lookupResult && (
            <div style={{
              padding: '10px 12px', borderRadius: 8,
              background: `${ACCENT}10`, border: `1px solid ${ACCENT}33`,
              display: 'flex', gap: 10, alignItems: 'center',
            }}>
              {lookupResult.cover_url && (
                <img src={lookupResult.cover_url} alt="" style={{ width: 32, height: 48, objectFit: 'cover', borderRadius: 3 }} />
              )}
              <div>
                <div style={{ fontSize: 12, color: ACCENT, fontWeight: 600 }}>{lookupResult.title}</div>
                {lookupResult.year && <div style={{ fontSize: 10, color: TEXT3 }}>{lookupResult.year}</div>}
              </div>
            </div>
          )}

          <div>
            <label style={{ fontSize: 10, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Confirmed Title</label>
            <input
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              style={{
                width: '100%', background: '#0a0a0f', border: `1px solid ${BORDER}`,
                borderRadius: 6, padding: '8px 10px', color: TEXT, fontSize: 13, outline: 'none',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 2 }}>
              <label style={{ fontSize: 10, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Series</label>
              <input
                value={form.series}
                onChange={e => setForm(f => ({ ...f, series: e.target.value }))}
                placeholder="Optional"
                style={{
                  width: '100%', background: '#0a0a0f', border: `1px solid ${BORDER}`,
                  borderRadius: 6, padding: '8px 10px', color: TEXT, fontSize: 13, outline: 'none',
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>#</label>
              <input
                type="number"
                value={form.series_order}
                onChange={e => setForm(f => ({ ...f, series_order: e.target.value }))}
                placeholder="Order"
                style={{
                  width: '100%', background: '#0a0a0f', border: `1px solid ${BORDER}`,
                  borderRadius: 6, padding: '8px 10px', color: TEXT, fontSize: 13, outline: 'none',
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Year</label>
              <input
                type="number"
                value={form.year}
                onChange={e => setForm(f => ({ ...f, year: e.target.value }))}
                placeholder="Read"
                style={{
                  width: '100%', background: '#0a0a0f', border: `1px solid ${BORDER}`,
                  borderRadius: 6, padding: '8px 10px', color: TEXT, fontSize: 13, outline: 'none',
                }}
              />
            </div>
          </div>

          {/* Properties */}
          <div>
            <label style={{ fontSize: 10, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 8 }}>Reading Properties</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {[...coreProps, ...secProps].map(p => {
                const active = form.properties.includes(p.id);
                const color = PROPERTY_COLORS[p.category] || ACCENT;
                return (
                  <button
                    key={p.id}
                    onClick={() => toggleProperty(p.id)}
                    style={{
                      padding: '3px 9px', borderRadius: 4, fontSize: 10, cursor: 'pointer',
                      fontFamily: "'JetBrains Mono', monospace",
                      background: active ? `${color}22` : 'transparent',
                      color: active ? color : TEXT3,
                      border: `1px solid ${active ? color + '55' : BORDER}`,
                      transition: 'all 0.15s',
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label style={{ fontSize: 10, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={3}
              placeholder="Werner's voice. Optional."
              style={{
                width: '100%', background: '#0a0a0f', border: `1px solid ${BORDER}`,
                borderRadius: 6, padding: '8px 10px', color: TEXT, fontSize: 13,
                resize: 'vertical', fontFamily: 'inherit', outline: 'none',
              }}
            />
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.foundational}
              onChange={e => setForm(f => ({ ...f, foundational: e.target.checked }))}
              style={{ accentColor: ACCENT }}
            />
            <span style={{ fontSize: 12, color: TEXT2 }}>Foundational — left permanent architecture in Werner's thinking</span>
          </label>
        </div>

        <div style={{ padding: '12px 24px', borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={save} disabled={saving || !form.title.trim()} style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background: form.title.trim() ? ACCENT : `${ACCENT}44`,
            color: '#0a0a0f', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Add to Library
          </button>
          <button onClick={onClose} style={{
            padding: '10px 16px', borderRadius: 8,
            background: 'transparent', color: TEXT2, border: `1px solid ${BORDER}`, fontSize: 13, cursor: 'pointer',
          }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Add Author Modal ───────────────────────────────────────────────────────────
function AddAuthorModal({ properties, onClose, onAdded }) {
  const feedback = useConfirmation();
  const [form, setForm] = useState({ name: '', notes: '', pantheon: true, full_works: false, properties: [] });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post('/library/authors', form);
      onAdded(data);
      feedback.confirm();
    } catch {
      feedback.fail();
    } finally {
      setSaving(false);
    }
  }

  const toggleProperty = (pid) => {
    setForm(f => ({
      ...f,
      properties: f.properties.includes(pid)
        ? f.properties.filter(x => x !== pid)
        : [...f.properties, pid],
    }));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)' }} onClick={onClose} />
      <div style={{
        position: 'relative', zIndex: 1, background: '#111',
        border: `1px solid ${BORDER}`, borderRadius: 16,
        width: '100%', maxWidth: 440, maxHeight: '80vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, color: TEXT, fontWeight: 700 }}>Add Author</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEXT2 }}><X size={16} /></button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 10, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Name</label>
            <input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Author name"
              autoFocus
              style={{
                width: '100%', background: '#0a0a0f', border: `1px solid ${BORDER}`,
                borderRadius: 6, padding: '8px 10px', color: TEXT, fontSize: 13, outline: 'none',
              }}
            />
          </div>

          <div>
            <label style={{ fontSize: 10, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Notes</label>
            <input
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="e.g. Harry Hole series only"
              style={{
                width: '100%', background: '#0a0a0f', border: `1px solid ${BORDER}`,
                borderRadius: 6, padding: '8px 10px', color: TEXT, fontSize: 13, outline: 'none',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.pantheon} onChange={e => setForm(f => ({ ...f, pantheon: e.target.checked }))} style={{ accentColor: ACCENT }} />
              <span style={{ fontSize: 12, color: TEXT2 }}>Pantheon</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.full_works} onChange={e => setForm(f => ({ ...f, full_works: e.target.checked }))} style={{ accentColor: ACCENT }} />
              <span style={{ fontSize: 12, color: TEXT2 }}>Full Works</span>
            </label>
          </div>

          <div>
            <label style={{ fontSize: 10, color: TEXT3, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 8 }}>Dominant Properties</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {properties.filter(p => p.category !== 'absence').map(p => {
                const active = form.properties.includes(p.id);
                const color = PROPERTY_COLORS[p.category] || ACCENT;
                return (
                  <button key={p.id} onClick={() => toggleProperty(p.id)} style={{
                    padding: '3px 9px', borderRadius: 4, fontSize: 10, cursor: 'pointer',
                    fontFamily: "'JetBrains Mono', monospace",
                    background: active ? `${color}22` : 'transparent',
                    color: active ? color : TEXT3,
                    border: `1px solid ${active ? color + '55' : BORDER}`,
                    transition: 'all 0.15s',
                  }}>
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div style={{ padding: '12px 24px', borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={save} disabled={saving || !form.name.trim()} style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background: form.name.trim() ? ACCENT : `${ACCENT}44`,
            color: '#0a0a0f', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Add to Library
          </button>
          <button onClick={onClose} style={{
            padding: '10px 16px', borderRadius: 8,
            background: 'transparent', color: TEXT2, border: `1px solid ${BORDER}`, fontSize: 13, cursor: 'pointer',
          }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Recommend Panel ────────────────────────────────────────────────────────────
function RecommendPanel({ onClose }) {
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState("What life haven't I lived yet that would expand my range?");

  const recommend = useCallback(async (q) => {
    setLoading(true);
    setResult('');
    try {
      const { data } = await api.post('/library/recommend', { question: q });
      setResult(data.recommendation);
    } catch {
      setResult('Recommendation engine unavailable.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    recommend(question);
  }, []); // eslint-disable-line

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div style={{
        position: 'relative', zIndex: 1, width: '100%', maxWidth: 520,
        height: '85vh', background: '#111',
        borderLeft: `1px solid ${BORDER}`, borderTop: `1px solid ${BORDER}`,
        borderTopLeftRadius: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Sparkles size={16} color={ACCENT} />
              <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, color: TEXT, fontWeight: 700 }}>Zephyr Recommendations</span>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEXT2 }}><X size={18} /></button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={question}
                onChange={e => setQuestion(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && recommend(question)}
                style={{
                  flex: 1, background: '#0a0a0f', border: `1px solid ${BORDER}`,
                  borderRadius: 6, padding: '8px 10px', color: TEXT, fontSize: 12, outline: 'none',
                }}
              />
              <button onClick={() => recommend(question)} disabled={loading} style={{
                padding: '8px 12px', borderRadius: 6, background: `${ACCENT}22`,
                color: ACCENT, border: `1px solid ${ACCENT}44`, cursor: 'pointer', fontSize: 12,
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              </button>
            </div>
          </div>

          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: TEXT3, fontSize: 13 }}>
              <Loader2 size={14} className="animate-spin" color={ACCENT} />
              Zephyr is thinking...
            </div>
          ) : (
            <div style={{ fontSize: 13, color: TEXT2, lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>{result}</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function Library({ embedded = false }) {
  const feedback = useConfirmation();
  const navigate = useNavigate();
  const [view, setView] = useState('pantheon'); // pantheon | series | properties
  const [authors, setAuthors] = useState([]);
  const [books, setBooks] = useState([]);
  const [properties, setProperties] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [authorBooks, setAuthorBooks] = useState([]);
  const [showAddAuthor, setShowAddAuthor] = useState(false);
  const [showRecommend, setShowRecommend] = useState(false);

  const load = useCallback(async () => {
    try {
      const [authRes, booksRes, propsRes, statsRes] = await Promise.all([
        api.get('/library/authors'),
        api.get('/library/books'),
        api.get('/library/properties'),
        api.get('/library/stats'),
      ]);
      setAuthors(authRes.data);
      setBooks(booksRes.data);
      setProperties(propsRes.data);
      setStats(statsRes.data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function seed() {
    setSeeding(true);
    try {
      const { data } = await api.post('/library/seed');
      feedback.confirm();
      await load();
    } catch {
      feedback.fail();
    } finally {
      setSeeding(false);
    }
  }

  function openAuthor(author) {
    const ab = books.filter(b => b.author_id === author.id);
    setAuthorBooks(ab);
    setSelectedAuthor(author);
    setSelectedBook(null);
  }

  function closeAuthor() {
    setSelectedAuthor(null);
    setAuthorBooks([]);
  }

  function onBookUpdated(updatedBook) {
    setBooks(prev => prev.map(b => b.id === updatedBook.id ? updatedBook : b));
    setAuthorBooks(prev => prev.map(b => b.id === updatedBook.id ? updatedBook : b));
    setSelectedBook(updatedBook);
  }

  function onBookDeleted(bookId) {
    setBooks(prev => prev.filter(b => b.id !== bookId));
    setAuthorBooks(prev => prev.filter(b => b.id !== bookId));
  }

  function onAuthorUpdated(updatedAuthor) {
    setAuthors(prev => prev.map(a => a.id === updatedAuthor.id ? updatedAuthor : a));
    setSelectedAuthor(updatedAuthor);
  }

  function onAuthorAdded(author) {
    setAuthors(prev => [...prev, author].sort((a, b) => a.name.localeCompare(b.name)));
    setShowAddAuthor(false);
    openAuthor(author);
  }

  function onBookAdded(book) {
    setBooks(prev => [...prev, book]);
    setAuthorBooks(prev => [...prev, book]);
    setSelectedBook(book);
  }

  // Filtered authors for pantheon view
  const pantheonAuthors = authors
    .filter(a => a.pantheon)
    .filter(a => !search || a.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  const fullWorksAuthors = pantheonAuthors.filter(a => a.full_works);
  const otherPantheon = pantheonAuthors.filter(a => !a.full_works);

  // Books by series for series view
  const seriesMap = {};
  books.forEach(b => {
    const key = b.series || '__standalone__';
    if (!seriesMap[key]) seriesMap[key] = [];
    seriesMap[key].push(b);
  });

  const seriesList = Object.entries(seriesMap)
    .filter(([key]) => key !== '__standalone__')
    .filter(([key]) => !search || key.toLowerCase().includes(search.toLowerCase()))
    .sort(([a], [b]) => a.localeCompare(b));

  const authorMap = {};
  authors.forEach(a => { authorMap[a.id] = a; });

  const TABS = [
    { id: 'pantheon',    label: 'Pantheon',   icon: Users },
    { id: 'series',      label: 'Series',     icon: List },
    { id: 'properties',  label: 'Properties', icon: Tag },
  ];

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 size={20} color={ACCENT} className="animate-spin" />
      </div>
    );
  }

  const isEmpty = authors.length === 0;

  return (
    <div style={{ minHeight: embedded ? 0 : '100vh', background: BG, color: TEXT }}>
      {/* Header */}
      <div style={{ position: 'relative', borderBottom: `1px solid ${BORDER}`, padding: '24px 24px 0' }}>
        <OverflowMenu
          placement="tab"
          items={[
            { icon: <Archive size={16} strokeWidth={1.5} />, label: 'View archive', onClick: () => navigate('/soulsync/library/archive') },
          ]}
        />
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: `${ACCENT}22`, border: `1px solid ${ACCENT}33`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <BookOpen size={16} color={ACCENT} />
              </div>
              <div>
                <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: TEXT, margin: 0 }}>Library</h1>
                <p style={{ fontSize: 11, color: TEXT3, margin: 0, marginTop: 1 }}>A Census of Lives Lived</p>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => setShowRecommend(true)}
              title="Ask Zephyr"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 12px', borderRadius: 8,
                background: `${ACCENT}15`, color: ACCENT,
                border: `1px solid ${ACCENT}33`, fontSize: 12, cursor: 'pointer',
              }}
            >
              <Sparkles size={13} />
              Zephyr
            </button>
            <button
              onClick={() => setShowAddAuthor(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 12px', borderRadius: 8,
                background: `${ACCENT}22`, color: ACCENT,
                border: `1px solid ${ACCENT}44`, fontSize: 12, cursor: 'pointer',
              }}
            >
              <Plus size={13} /> Author
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div style={{
            display: 'flex', gap: 32, marginBottom: 20,
            paddingBottom: 20, borderBottom: `1px solid ${BORDER}`,
          }}>
            <StatBadge value={stats.books} label="Books" />
            <StatBadge value={stats.pantheon} label="Pantheon" />
            <StatBadge value={stats.full_works} label="Full Works" />
            <StatBadge value={stats.foundational} label="Foundational" />
          </div>
        )}

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <Search size={14} color={TEXT3} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search authors, series..."
            style={{
              width: '100%', background: CARD, border: `1px solid ${BORDER}`,
              borderRadius: 8, padding: '8px 12px 8px 34px', color: TEXT, fontSize: 13, outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0 }}>
          {TABS.map(tab => {
            const active = view === tab.id;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setView(tab.id)}
                style={{
                  padding: '8px 16px 12px', fontSize: 12, cursor: 'pointer',
                  background: 'none', border: 'none',
                  color: active ? ACCENT : TEXT3,
                  borderBottom: `2px solid ${active ? ACCENT : 'transparent'}`,
                  display: 'flex', alignItems: 'center', gap: 6,
                  transition: 'color 0.15s',
                }}
              >
                <Icon size={13} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '20px 24px', maxWidth: 900, margin: '0 auto' }}>

        {/* Seed prompt */}
        {isEmpty && (
          <div style={{
            padding: '32px', borderRadius: 12, textAlign: 'center',
            background: `${ACCENT}08`, border: `1px solid ${ACCENT}22`,
            marginBottom: 24,
          }}>
            <BookOpen size={32} color={ACCENT} style={{ marginBottom: 12, opacity: 0.7 }} />
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: TEXT, marginBottom: 8 }}>
              Library is Empty
            </div>
            <div style={{ fontSize: 13, color: TEXT2, marginBottom: 20, maxWidth: 400, margin: '0 auto 20px' }}>
              Seed the 59 Pantheon authors, reading properties, and foundational data to begin.
            </div>
            <button onClick={seed} disabled={seeding} style={{
              padding: '12px 24px', borderRadius: 10,
              background: ACCENT, color: '#0a0a0f', border: 'none',
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}>
              {seeding ? <Loader2 size={16} className="animate-spin" /> : <BookOpen size={16} />}
              Seed Library
            </button>
          </div>
        )}

        {/* Pantheon View */}
        {view === 'pantheon' && !isEmpty && (
          <div>
            {/* Full Works */}
            {fullWorksAuthors.length > 0 && (
              <div style={{ marginBottom: 32 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <span style={{ fontSize: 11, color: ACCENT, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                    Full Works
                  </span>
                  <span style={{ fontSize: 10, color: TEXT3, fontFamily: "'JetBrains Mono', monospace" }}>{fullWorksAuthors.length}</span>
                  <span style={{ fontSize: 11, color: TEXT3 }}>— read everything by these authors</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                  {fullWorksAuthors.map(a => (
                    <AuthorCard
                      key={a.id}
                      author={a}
                      bookCount={books.filter(b => b.author_id === a.id).length}
                      properties={properties}
                      onClick={() => openAuthor(a)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Pantheon */}
            {otherPantheon.length > 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <span style={{ fontSize: 11, color: TEXT2, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                    Pantheon
                  </span>
                  <span style={{ fontSize: 10, color: TEXT3, fontFamily: "'JetBrains Mono', monospace" }}>{otherPantheon.length}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                  {otherPantheon.map(a => (
                    <AuthorCard
                      key={a.id}
                      author={a}
                      bookCount={books.filter(b => b.author_id === a.id).length}
                      properties={properties}
                      onClick={() => openAuthor(a)}
                    />
                  ))}
                </div>
              </div>
            )}

            {pantheonAuthors.length === 0 && search && (
              <div style={{ textAlign: 'center', color: TEXT3, fontSize: 13, padding: '40px 0' }}>
                No authors match "{search}"
              </div>
            )}
          </div>
        )}

        {/* Series View */}
        {view === 'series' && (
          <div>
            {seriesList.length === 0 && (
              <div style={{ textAlign: 'center', color: TEXT3, fontSize: 13, padding: '40px 0' }}>
                {search ? `No series match "${search}"` : 'No series added yet'}
              </div>
            )}
            {seriesList.map(([series, sbooks]) => {
              const firstBook = sbooks[0];
              const author = firstBook ? authorMap[firstBook.author_id] : null;
              const sortedBooks = [...sbooks].sort((a, b) => (a.series_order || 99) - (b.series_order || 99));
              const hasFoundational = sbooks.some(b => b.foundational);

              return (
                <div key={series} style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, color: TEXT, fontWeight: 600 }}>
                      {series}
                    </span>
                    {hasFoundational && <Star size={11} color={ACCENT} fill={ACCENT} />}
                    {author && (
                      <span style={{ fontSize: 11, color: TEXT3 }}>— {author.name}</span>
                    )}
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TEXT3 }}>
                      {sbooks.length} {sbooks.length === 1 ? 'book' : 'books'}
                    </span>
                  </div>
                  <div style={{ background: CARD, borderRadius: 10, border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
                    {sortedBooks.map((b, i) => (
                      <div key={b.id} style={{ borderBottom: i < sortedBooks.length - 1 ? `1px solid ${BORDER}` : 'none' }}>
                        <BookRow book={b} onClick={() => setSelectedBook(b)} />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Properties View */}
        {view === 'properties' && (
          <div>
            {['core', 'secondary'].map(category => {
              const catProps = properties.filter(p => p.category === category);
              return (
                <div key={category} style={{ marginBottom: 32 }}>
                  <div style={{ fontSize: 11, color: PROPERTY_COLORS[category] || ACCENT, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 14 }}>
                    {category === 'core' ? 'Core Properties' : 'Secondary Properties'}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {catProps.map(p => {
                      const propBooks = books.filter(b => b.properties && b.properties.includes(p.id));
                      const propAuthors = [...new Set(propBooks.map(b => b.author_id))].map(id => authorMap[id]).filter(Boolean);
                      const color = PROPERTY_COLORS[p.category] || ACCENT;

                      return (
                        <div key={p.id} style={{ background: CARD, borderRadius: 10, border: `1px solid ${BORDER}`, padding: '14px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <PropertyPill id={p.id} category={p.category} label={p.label} />
                                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: TEXT3 }}>
                                  {propBooks.length} books · {propAuthors.length} authors
                                </span>
                              </div>
                              <div style={{ fontSize: 11, color: TEXT3, lineHeight: 1.5 }}>{p.description}</div>
                            </div>
                          </div>
                          {propAuthors.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {propAuthors.slice(0, 8).map(a => (
                                <button
                                  key={a.id}
                                  onClick={() => openAuthor(a)}
                                  style={{
                                    padding: '2px 8px', borderRadius: 4, fontSize: 10,
                                    background: 'transparent', cursor: 'pointer',
                                    color: TEXT2, border: `1px solid ${BORDER}`,
                                    fontFamily: "'Inter', sans-serif",
                                    transition: 'border-color 0.15s',
                                  }}
                                  onMouseEnter={e => { e.currentTarget.style.borderColor = `${color}55`; e.currentTarget.style.color = color; }}
                                  onMouseLeave={e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.color = TEXT2; }}
                                >
                                  {a.name}
                                </button>
                              ))}
                              {propAuthors.length > 8 && (
                                <span style={{ fontSize: 10, color: TEXT3, padding: '2px 6px' }}>+{propAuthors.length - 8} more</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Panels */}
      {selectedAuthor && !selectedBook && (
        <AuthorPanel
          author={selectedAuthor}
          books={authorBooks}
          properties={properties}
          allProperties={properties}
          onClose={closeAuthor}
          onAuthorUpdate={onAuthorUpdated}
          onBookSelect={book => setSelectedBook(book)}
        />
      )}

      {selectedBook && (
        <BookPanel
          book={selectedBook}
          authorName={authorMap[selectedBook.author_id]?.name}
          properties={properties}
          onClose={() => setSelectedBook(null)}
          onUpdate={onBookUpdated}
          onDelete={onBookDeleted}
        />
      )}

      {showAddAuthor && (
        <AddAuthorModal
          properties={properties}
          onClose={() => setShowAddAuthor(false)}
          onAdded={onAuthorAdded}
        />
      )}

      {/* AddBook triggered from AuthorPanel via onAdded → goes back to authorPanel  */}
      {showRecommend && <RecommendPanel onClose={() => setShowRecommend(false)} />}
    </div>
  );
}
